import { ClassificationResult } from '@jump-tech-frontend/domain';

/**
 * How much validation detail the component renders. `simplified` shows the customer-facing
 * banner and quality/confidence bars; `advanced` adds full detection detail and the
 * bounding-box overlay (later increment). Whether any validation runs at all is driven by the
 * `checks` input, not the mode: with no checks the component is capture-and-preview only.
 */
export type CaptureMode = 'simplified' | 'advanced';

/** Bounds for the client-side downscale applied before preview/classification. */
export interface ResizeOptions {
  readonly maxEdgePx: number;
  readonly quality: number;
}

/**
 * Faithful default from the prod cards `ImageManipulationService`: longest edge 4000px, JPEG
 * quality 0.93. Hosts that persist the data URL locally (e.g. the portal, localStorage
 * quota) may pass a smaller bound.
 */
export const DEFAULT_RESIZE: ResizeOptions = { maxEdgePx: 4000, quality: 0.93 };

export type CaptureRejectionReason = 'file-type' | 'file-too-large';

/** A picked file that failed the pre-resize guard. */
export interface CaptureRejection {
  readonly fileName: string;
  readonly reason: CaptureRejectionReason;
}

/**
 * One captured image. `result` is absent in `plain` mode and populated once a classifier has
 * validated the image (`simplified`/`advanced`). This is the value the component emits; each
 * host maps it onto its own persistence.
 */
export interface CapturedImage {
  /** Resized JPEG data URL. */
  readonly image: string;
  /** Classification outcome, present once validated. */
  readonly result?: ClassificationResult;
}
